/**
 * HeadsManager.jsx — FIXED
 * FIXES:
 *   - Input focus stays after each keystroke (was losing focus due to state re-render)
 *   - Visually distinct "Add Head" section with proper design
 *   - Controlled input with stable ref to prevent cursor loss
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { Plus, Trash2, TrendingUp, TrendingDown, Lock } from 'lucide-react';
import { getHeads, createHead, deleteHead } from '../../api/yea.api';
import client from '../../api/client';
import Button from '../../components/common/Button';
import '../accounting/yea.css';

const HeadsManager = ({ societyId }) => {
  const [incomeHeads,  setIncomeHeads]  = useState([]);
  const [expenseHeads, setExpenseHeads] = useState([]);
  const [loading,      setLoading]      = useState(true);
  // Use separate controlled state for each input - NOT inside mapped components
  const [newIncome,    setNewIncome]    = useState('');
  const [newExpense,   setNewExpense]   = useState('');
  const [addingIncome, setAddingIncome] = useState(false);
  const [addingExpense,setAddingExpense]= useState(false);
  const [seeding,      setSeeding]      = useState(false);

  // Stable refs so focus never moves
  const incomeRef  = useRef(null);
  const expenseRef = useRef(null);

  const handleSeedDefaults = async () => {
    if (!window.confirm('This will add the default income and expense heads to this society. Continue?')) return;
    setSeeding(true);
    try {
      await client.post(`/societies/${societyId}/heads/seed-defaults`);
      toast.success('Default heads loaded successfully');
      fetch();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSeeding(false); }
  };

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [inc, exp] = await Promise.all([
        getHeads(societyId, 'income'),
        getHeads(societyId, 'expense'),
      ]);
      setIncomeHeads(inc.data.heads);
      setExpenseHeads(exp.data.heads);
    } catch { toast.error('Failed to load heads'); }
    finally { setLoading(false); }
  }, [societyId]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleAdd = async (type) => {
    const name = type === 'income' ? newIncome.trim() : newExpense.trim();
    if (!name) { toast.error('Head name is required'); return; }

    if (type === 'income') setAddingIncome(true);
    else setAddingExpense(true);

    try {
      await createHead(societyId, { name, type });
      toast.success(`${type === 'income' ? 'Income' : 'Expense'} head added`);
      if (type === 'income') { setNewIncome(''); setTimeout(() => incomeRef.current?.focus(), 50); }
      else { setNewExpense(''); setTimeout(() => expenseRef.current?.focus(), 50); }
      // Re-fetch only the relevant list
      const r = await getHeads(societyId, type);
      if (type === 'income') setIncomeHeads(r.data.heads);
      else setExpenseHeads(r.data.heads);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally {
      if (type === 'income') setAddingIncome(false);
      else setAddingExpense(false);
    }
  };

  const handleDelete = async (headId, type) => {
    if (!window.confirm('Remove this head?')) return;
    try {
      await deleteHead(societyId, headId);
      toast.success('Head removed');
      const r = await getHeads(societyId, type);
      if (type === 'income') setIncomeHeads(r.data.heads);
      else setExpenseHeads(r.data.heads);
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div className="animate-in">
      <div style={{ marginBottom:'1.5rem' }}>
        <h2>Account Heads</h2>
        <p className="text-sm text-muted" style={{ marginTop:'0.2rem' }}>
          Manage income and expense heads. Shared across all financial years for this society.
        </p>
        {(incomeHeads.length === 0 && expenseHeads.length === 0) && (
          <div style={{ marginTop:'0.75rem', padding:'0.85rem 1rem', background:'var(--info-bg)', border:'1px solid rgba(91,156,246,0.2)', borderRadius:'var(--radius-sm)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem', flexWrap:'wrap' }}>
            <span style={{ fontSize:'0.85rem', color:'var(--info)' }}>No heads yet. Load the standard set of income and expense heads to get started quickly.</span>
            <Button size="sm" loading={seeding} onClick={handleSeedDefaults}>Load Default Heads</Button>
          </div>
        )}
      </div>

      <div className="heads-grid">
        {/* ── INCOME HEADS ── */}
        <div className="card">
          <div style={{ display:'flex', alignItems:'center', gap:'0.6rem', marginBottom:'1.25rem', paddingBottom:'0.85rem', borderBottom:'1px solid var(--border-subtle)' }}>
            <div style={{ width:32, height:32, background:'var(--success-bg)', borderRadius:'var(--radius-sm)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <TrendingUp size={16} color="var(--success)" />
            </div>
            <div>
              <div style={{ fontWeight:600, color:'var(--success)', fontSize:'0.9rem' }}>Income Heads</div>
              <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{incomeHeads.length} head{incomeHeads.length !== 1 ? 's' : ''}</div>
            </div>
          </div>

          {/* Add input - stable, not inside map */}
          <div style={{ display:'flex', gap:'0.5rem', marginBottom:'1rem', padding:'0.85rem', background:'var(--bg-raised)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
            <input
              ref={incomeRef}
              type="text"
              className="field-input"
              placeholder="Type income head name..."
              value={newIncome}
              onChange={e => setNewIncome(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd('income'); } }}
              style={{ flex:1 }}
            />
            <Button size="sm" icon={Plus} loading={addingIncome} onClick={() => handleAdd('income')} type="button">Add</Button>
          </div>

          <div className="head-list">
            {incomeHeads.length === 0
              ? <div style={{ padding:'1.5rem', textAlign:'center', color:'var(--text-muted)', fontSize:'0.85rem', background:'var(--bg-raised)', borderRadius:'var(--radius-sm)', border:'1px dashed var(--border)' }}>
                  No income heads yet. Add one above.
                </div>
              : incomeHeads.map(h => (
                <div key={h._id} className="head-item">
                  <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                    <div style={{ width:6, height:6, borderRadius:'50%', background:'var(--success)', flexShrink:0 }} />
                    <span style={{ fontSize:'0.875rem', color:'var(--text)' }}>{h.name}</span>
                  </div>
                  <Button size="xs" variant="danger" icon={Trash2} onClick={() => handleDelete(h._id, 'income')} />
                </div>
              ))
            }
          </div>
        </div>

        {/* ── EXPENSE HEADS ── */}
        <div className="card">
          <div style={{ display:'flex', alignItems:'center', gap:'0.6rem', marginBottom:'1.25rem', paddingBottom:'0.85rem', borderBottom:'1px solid var(--border-subtle)' }}>
            <div style={{ width:32, height:32, background:'var(--danger-bg)', borderRadius:'var(--radius-sm)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <TrendingDown size={16} color="var(--danger)" />
            </div>
            <div>
              <div style={{ fontWeight:600, color:'var(--danger)', fontSize:'0.9rem' }}>Expense Heads</div>
              <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{expenseHeads.length} head{expenseHeads.length !== 1 ? 's' : ''}</div>
            </div>
          </div>

          {/* Add input - stable, not inside map */}
          <div style={{ display:'flex', gap:'0.5rem', marginBottom:'1rem', padding:'0.85rem', background:'var(--bg-raised)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
            <input
              ref={expenseRef}
              type="text"
              className="field-input"
              placeholder="Type expense head name..."
              value={newExpense}
              onChange={e => setNewExpense(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd('expense'); } }}
              style={{ flex:1 }}
            />
            <Button size="sm" icon={Plus} loading={addingExpense} onClick={() => handleAdd('expense')} type="button">Add</Button>
          </div>

          <div className="head-list">
            {expenseHeads.length === 0
              ? <div style={{ padding:'1.5rem', textAlign:'center', color:'var(--text-muted)', fontSize:'0.85rem', background:'var(--bg-raised)', borderRadius:'var(--radius-sm)', border:'1px dashed var(--border)' }}>
                  No expense heads yet. Add one above.
                </div>
              : expenseHeads.map(h => (
                <div key={h._id} className="head-item">
                  <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                    <div style={{ width:6, height:6, borderRadius:'50%', background:'var(--danger)', flexShrink:0 }} />
                    <span style={{ fontSize:'0.875rem', color:'var(--text)' }}>{h.name}</span>
                  </div>
                  <Button size="xs" variant="danger" icon={Trash2} onClick={() => handleDelete(h._id, 'expense')} />
                </div>
              ))
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeadsManager;
